import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useOutletContext } from 'react-router-dom';

function CookiePolicyMain() {
    const { setCurrentPage } = useOutletContext();

    useEffect(() => {
        setCurrentPage("Cookie Policy");
    }, [setCurrentPage]);

    const sections = [
        { id: 'what-are-cookies', label: 'What are Cookies', icon: 'cookie' },
        { id: 'types-of-cookies', label: 'Types of Cookies', icon: 'category' },
        { id: 'essential-cookies', label: 'Essential Cookies', icon: 'security' },
        { id: 'functional-cookies', label: 'Functional Cookies', icon: 'settings' },
        { id: 'preferences', label: 'Managing Preferences', icon: 'tune' },
    ];

    const [activeSection, setActiveSection] = useState(sections[0].id);

    useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: '-20% 0px -70% 0px',
            threshold: 0
        };

        const observerCallback = (entries) => {
            entries.forEach((entry) => {
            if (entry.isIntersecting) {
                setActiveSection(entry.target.id);
            }
            });
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);

        sections.forEach((section) => {
            const element = document.getElementById(section.id);
            if (element) observer.observe(element);
        });

        return () => observer.disconnect();
    }, []);

    const scrollToSection = (id) => {
        const element = document.getElementById(id);

        if (element) {
            const offset = 80;
            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = element.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <main className="relative flex min-h-[calc(100vh-64px)] w-full carbon-bg">
            <div className="layout-container max-w-[1440px] mx-auto flex w-full">
                <aside className="hidden lg:flex flex-col w-80 border-r border-border-color p-8 sticky top-16 h-full">
                    <div className="flex flex-col gap-6 sticky top-[140px]">
                        <div className="mb-8">
                            <h3 className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-4">Policy Sections</h3>
                            <nav className="flex flex-col gap-1">
                                {sections.map((section) => {
                                    const isActive = activeSection === section.id;
                                    
                                    return (
                                    <button
                                        key={section.id}
                                        onClick={() => scrollToSection(section.id)}
                                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all border ${
                                        isActive
                                            ? "bg-primary/10 text-primary border-primary/20 font-bold"
                                            : "hover:bg-surface text-slate-400 hover:text-white border-transparent"
                                        }`}
                                    >
                                        <span className="material-symbols-outlined text-xl">
                                        {section.icon}
                                        </span>
                                        <span className="text-sm">
                                        {section.label}
                                        </span>
                                    </button>
                                    );
                                })}
                            </nav>
                        </div>
                        
                        <div className="mt-auto p-4 rounded-xl bg-surface/50 border border-border-color">
                            <p className="text-xs text-slate-400 leading-relaxed mb-3">Want to understand more about your privacy?</p>
                            <button className="w-full py-2 bg-surface hover:bg-border-color border border-border-color text-xs font-bold rounded-lg transition-colors">Privacy Center</button>
                        </div>
                    </div>
                </aside>
                <div className="flex-1 overflow-y-auto content-scroll">
                    <div className="max-w-[850px] mx-auto px-6 py-12 lg:px-12 lg:py-16">
                        <div className="mb-12 border-b border-border-color pb-8">
                            <div className="flex items-center gap-2 mb-4">
                                <span className="px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold uppercase tracking-wider rounded-full border border-primary/30">Compliance</span>
                                <span className="text-slate-500 text-sm">•</span>
                                <span className="text-slate-500 text-sm">Last Updated: February 09, 2026</span>
                            </div>
                            <h1 className="text-5xl font-black text-white tracking-tight mb-4 leading-tight">Cookie Policy</h1>
                            <p className="text-slate-400 text-lg leading-relaxed max-w-2xl">
                                This policy explains how ACCLiveTelemetry uses cookies and similar technologies to provide, improve, and protect our platform.
                            </p>
                        </div>
                        <section className="mb-16 scroll-mt-24" id="what-are-cookies">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-8 w-1 bg-primary rounded-full"></div>
                                <h2 className="text-2xl font-bold text-white tracking-tight">1. What are Cookies</h2>
                            </div>
                            <div className="space-y-4 text-slate-300 leading-relaxed">
                                <p>Cookies are small text files stored on your device when you visit websites. They help the website remember your actions and preferences over a period of time, so you don't have to keep re-entering them whenever you come back to the site or browse from one page to another.</p>
                                <p>On ACCLiveTelemetry, we use cookies to maintain your telemetry session, remember your dashboard layouts, and ensure the security of your Steam authentication.</p>
                            </div>
                        </section>
                        <section className="mb-16 scroll-mt-24" id="types-of-cookies">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-8 w-1 bg-primary rounded-full"></div>
                                <h2 className="text-2xl font-bold text-white tracking-tight">2. Types of Cookies We Use</h2>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-surface rounded-xl p-6 border border-border-color">
                                    <h3 className="text-primary font-bold mb-3 flex items-center gap-2">
                                        <span className="material-symbols-outlined text-sm">timer</span>
                                        Session Cookies
                                    </h3>
                                    <p className="text-slate-400 text-sm leading-relaxed">Temporary cookies that expire once you close your browser. Used mainly for handling your active telemetry stream and live session data.</p>
                                </div>
                                <div className="bg-surface rounded-xl p-6 border border-border-color">
                                    <h3 className="text-primary font-bold mb-3 flex items-center gap-2">
                                        <span className="material-symbols-outlined text-sm">history</span>
                                        Persistent Cookies
                                    </h3>
                                    <p className="text-slate-400 text-sm leading-relaxed">Cookies that remain on your device for a set period. Used to remember your "Race Engineer" preferences and UI customization settings.</p>
                                </div>
                            </div>
                        </section>
                        <section className="mb-16 scroll-mt-24" id="essential-cookies">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-8 w-1 bg-primary rounded-full"></div>
                                <h2 className="text-2xl font-bold text-white tracking-tight">3. Essential Cookies</h2>
                            </div>
                            <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
                                <p className="text-slate-300 mb-6">These cookies are strictly necessary to provide you with services available through our website and to use some of its features, such as access to secure areas.</p>
                                <div className="space-y-4">
                                    <div className="flex items-start gap-4 p-4 bg-surface/50 rounded-lg border border-border-color/50">
                                        <span className="material-symbols-outlined text-primary mt-1">vpn_key</span>
                                        <div>
                                            <h4 className="text-white font-bold text-sm">Authentication &amp; Security</h4>
                                            <p className="text-slate-400 text-xs mt-1">Maintains your Steam OpenID session and prevents cross-site request forgery attacks. These cannot be disabled.</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4 p-4 bg-surface/50 rounded-lg border border-border-color/50">
                                        <span className="material-symbols-outlined text-primary mt-1">monitoring</span>
                                        <div>
                                            <h4 className="text-white font-bold text-sm">Session Management</h4>
                                            <p className="text-slate-400 text-xs mt-1">Tracks the link between your local sync client and the web dashboard for real-time data transmission.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                        <section className="mb-16 scroll-mt-24" id="functional-cookies">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-8 w-1 bg-primary rounded-full"></div>
                                <h2 className="text-2xl font-bold text-white tracking-tight">4. Functional Cookies</h2>
                            </div>
                            <div className="space-y-6">
                                <p className="text-slate-300">Functional cookies allow the platform to remember choices you make (such as your preferred telemetry units, metric vs imperial) and provide enhanced, more personal features.</p>
                                <div className="bg-surface/40 p-5 rounded-xl border border-border-color/50">
                                    <div className="text-white font-bold mb-3 flex items-center gap-2">
                                        <span className="material-symbols-outlined text-primary">analytics</span>
                                        Telemetry Preferences
                                    </div>
                                    <ul className="list-disc pl-5 space-y-2 text-slate-400 text-sm">
                                        <li>Remembers your preferred chart layouts for tire temperature and brake wear.</li>
                                        <li>Stores your default "Race Engineer" sharing permissions for teammates.</li>
                                        <li>Keeps track of your recent lap comparisons to speed up dashboard loading.</li>
                                    </ul>
                                </div>
                            </div>
                        </section>
                        <section className="mb-16 scroll-mt-24" id="preferences">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-8 w-1 bg-primary rounded-full"></div>
                                <h2 className="text-2xl font-bold text-white tracking-tight">5. Managing Preferences</h2>
                            </div>
                            <div className="prose prose-invert prose-slate max-w-none text-slate-300">
                                <p>Most web browsers allow you to control cookies through their settings preferences. However, if you limit the ability of websites to set cookies, you may worsen your overall user experience, as it will no longer be personalized to you.</p>
                                <div className="mt-6 p-6 bg-surface border border-border-color rounded-xl">
                                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                                        <div>
                                            <h4 className="text-white font-bold mb-1">Consent Preferences</h4>
                                            <p className="text-slate-400 text-sm">You can reset your cookie consent at any time using the button below.</p>
                                        </div>
                                        <button className="whitespace-nowrap bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 px-6 py-2 rounded-lg font-bold transition-all">
                                            Manage Cookie Settings
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </section>
                        <div className="mt-20 p-10 rounded-2xl bg-gradient-to-br from-surface to-background-dark border border-border-color text-center relative overflow-hidden">
                            <div className="absolute -right-10 -top-10 text-primary/10 opacity-20 rotate-12">
                                <span className="material-symbols-outlined" style={{fontSize: "200px"}}>cookie</span>
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-4">Privacy Questions?</h3>
                            <p className="text-slate-400 mb-8 max-w-md mx-auto">Our support team is here to help if you have questions about how we handle your data or use browser cookies.</p>
                            <div className="flex flex-wrap justify-center gap-4">
                                <button className="bg-primary hover:bg-primary/90 text-white font-bold py-3 px-8 rounded-lg transition-all shadow-lg shadow-primary/20">Contact Support</button>
                                <button className="bg-surface hover:bg-border-color text-white border border-border-color font-bold py-3 px-8 rounded-lg transition-all">Download PDF Policy</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}

export default CookiePolicyMain;